


























































































































import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import userStore from '@/modules/auth/auth.store'
import tournamentStore from '@/modules/tournament/tournament.store'
import { getModule } from 'vuex-module-decorators'
import { HubConnection } from '@microsoft/signalr'
import { getRealtimeConnection, realtimeEventTarget } from '@/utils/realtime'
import { CancelError, CancellationToken } from '@/utils/cancel'
import { ResponseStatus, TournamentAPI } from '@/api/tournament'
import ServerError from '@/utils/server_error'
import { getElementScrollRemaining } from '@/utils/window'
import { TournamentTeamEntity, TournamentTeamRole } from '@/api/tournamentTeam'
import UserAvatar from '@/modules/shared/components/UserAvatar'

@Component({
  components: {
    UserAvatar
  }
})
export default class InstantMatchTournamentPlayerList extends Vue {
  @Prop({ required: true }) tournamentId!: string
  @Prop({ required: true }) isVisible!: boolean

  isLoading: boolean = false
  isInitializing: boolean = true
  tournamentTeams: TournamentTeamEntity[] = []
  page: number = 1
  hasMorePage: boolean = true
  defaultAvatar: string = '/img/logo-v13.png'

  selectedTournamentTeam: TournamentTeamEntity | null = null

  realtimeConnection!: HubConnection
  realtimeConnectionCancellationToken = new CancellationToken()

  /* Hooks */
  async created() {
    this.isInitializing = true
    await this.fetchTournamentTeams()
    await this.subscribeRealtimeEvents()
    realtimeEventTarget.addEventListener(
      'connect',
      this.subscribeRealtimeEvents
    )
    realtimeEventTarget.addEventListener(
      'reconnected',
      this.subscribeRealtimeEvents
    )

    this.isInitializing = false
  }

  async beforeDestroy() {
    await this.unsubscribeRealtimeEvents()
    realtimeEventTarget.removeEventListener(
      'connect',
      this.subscribeRealtimeEvents
    )
    realtimeEventTarget.removeEventListener(
      'reconnected',
      this.subscribeRealtimeEvents
    )
  }

  async subscribeRealtimeEvents() {
    try {
      this.realtimeConnection = await getRealtimeConnection(
        this.realtimeConnectionCancellationToken
      )
    } catch (err) {
      if (err instanceof CancelError) {
        return
      }

      this.$toast.error(
        this.$t('TournamentDetailsTeams.FailConnectServerMessage')
      )
      console.error(err)
    }
    this.realtimeConnection.on(
      'tournamentTeamsUpdated',
      this.onTournamentTeamsUpdated
    )
  }

  async unsubscribeRealtimeEvents() {
    this.realtimeConnectionCancellationToken.cancel()
    if (this.realtimeConnection) {
      this.realtimeConnection.off(
        'tournamentTeamsUpdated',
        this.onTournamentTeamsUpdated
      )
    }
  }

  async onTournamentTeamsUpdated() {
    this.page = 1
    await this.fetchTournamentTeams()
  }

  /* Watchers */
  @Watch('isVisible')
  async onVisibilityChanged(visible: boolean) {
    if (visible) {
      this.isInitializing = true
      await this.fetchTournamentTeams()
      this.isInitializing = false
    } else {
      this.$nextTick(() => {
        this.tournamentTeams = []
        this.page = 1
        this.hasMorePage = true
        this.selectedTournamentTeam = null
      })
    }
  }

  /* Methods */
  async fetchTournamentTeams() {
    if (this.isLoading || !this.tournamentId) return

    this.isLoading = true
    try {
      const response = await TournamentAPI.getTournamentTeams(
        this.tournamentId,
        this.page,
        true
      )
      const payload = response.data.payload
      if (response.data.status === ResponseStatus.Ok && payload) {
        this.tournamentTeams = [...this.tournamentTeams, ...payload.items]
        this.hasMorePage = payload.hasMorePage
        this.page++
      } else {
        this.tournamentTeams = []
        this.page = 1
        this.hasMorePage = true
      }
    } catch (err) {
      if (err instanceof ServerError) {
        this.$toast.error(err.getMessage())
      } else {
        this.$toast.error(this.$t('Loading.SystemErrorMessage'))
        console.error(err)
      }
    } finally {
      this.isLoading = false
    }
  }

  async scrollBehavior(event: Event) {
    if (!this.hasMorePage) return
    if (getElementScrollRemaining(event.target as HTMLElement) <= 20) {
      await this.fetchTournamentTeams()
    }
  }

  onTournamentTeamSelected(team: TournamentTeamEntity) {
    this.selectedTournamentTeam = team
  }

  getCaptainAvatar(team: TournamentTeamEntity): string {
    let captain = team!.players!.find(p => p.role === 'Captain')
    return !captain ? '' : captain.avatar!
  }

  getCaptainName(team: TournamentTeamEntity): string {
    let captain = team!.players!.find(p => p.role === 'Captain')
    return !captain ? '' : captain.nickname!
  }

  /* Computed Properties */
  get TournamentTeamRole() {
    return TournamentTeamRole
  }
}
